import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Stethoscope, HeartPulse, ShieldCheck, Loader2, Check, X, FileSignature, Radar } from "lucide-react";

// Phase 2: appears once both other-driver docs are uploaded. Client requests a
// complimentary RN intake, signs the (placeholder) consent forms, then sees the
// "locating a nurse" state.
export default function RnIntakeCard({ cases }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const target = (cases || []).find((c) => c.status === "active") || (cases || [])[0];

  const load = () => {
    if (!target) { setLoading(false); return; }
    api.get(`/cases/${target.case_id}/rn-intake`)
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [target?.case_id]);

  if (loading || !target || !data) return null;
  const gate = data.gate || { unlocked: true, legacy: true };
  // Verified-flow cases: show a locked checklist until every requirement clears.
  if (!gate.legacy && !gate.unlocked && data.status === "none") {
    return (
      <div className="mt-6 rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden" data-testid="rn-locked-card">
        <div className="bg-navy text-white p-6 flex items-start gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0"><HeartPulse className="w-6 h-6 text-slate-400" /></div>
          <div>
            <div className="inline-flex items-center gap-1.5 text-amber-300 text-[11px] font-semibold uppercase tracking-[0.15em]"><Stethoscope className="w-3.5 h-3.5" /> Locked</div>
            <h2 className="font-heading text-xl font-bold mt-0.5">Request Crash Ready Intake Nurse</h2>
            <p className="text-sm text-slate-300 mt-1 max-w-md" data-testid="rn-locked-message">Additional verification is required before a licensed Crash Ready Triage RN can be notified.</p>
          </div>
        </div>
        <div className="bg-white p-6">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Still needed</p>
          <div className="space-y-1.5" data-testid="rn-locked-checklist">
            {[...gate.missing, ...data.missing_docs.map((d) => `Upload: ${d}`)].map((m) => (
              <div key={m} className="flex items-center gap-2 text-sm text-slate-600"><X className="w-4 h-4 text-amber-500 shrink-0" /> {m}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  // Only show the card when the case qualifies or an intake is already in flight.
  if (!data.qualified && data.status === "none") return null;

  const locating = data.status === "locating";
  const assigned = data.status === "assigned" || data.status === "completed";

  return (
    <div className="mt-6 rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden" data-testid="rn-intake-card">
      <div className="bg-navy text-white p-6 relative">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 15% 0%, rgba(56,189,248,0.4), transparent 55%)" }} />
        <div className="relative flex items-start gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0"><HeartPulse className="w-6 h-6 text-cyan-300" /></div>
          <div>
            <div className="inline-flex items-center gap-1.5 text-cyan-300 text-[11px] font-semibold uppercase tracking-[0.15em]"><Stethoscope className="w-3.5 h-3.5" /> Premium benefit</div>
            <h2 className="font-heading text-xl font-bold mt-0.5">Request Crash Ready Intake Nurse</h2>
            <p className="text-sm text-slate-300 mt-1 max-w-md">Your claim qualifies for a complimentary medical intake performed by a Crash Ready Registered Nurse.</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6">
        {locating ? (
          <div className="flex items-center gap-3 rounded-2xl bg-cobalt-light/50 p-4" data-testid="rn-locating-state">
            <Radar className="w-6 h-6 text-cobalt animate-pulse shrink-0" />
            <div>
              <p className="font-semibold text-navy">We are locating the next available Crash Ready Registered Nurse.</p>
              <p className="text-sm text-slate-500 mt-0.5">Thank you — you'll be notified the moment a nurse accepts your intake.</p>
            </div>
          </div>
        ) : assigned ? (
          <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 p-4">
            <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
            <p className="font-semibold text-emerald-700">A Crash Ready RN {data.assigned_nurse ? `(${data.assigned_nurse})` : ""} is handling your intake.</p>
          </div>
        ) : (
          <>
            <button onClick={() => setShowConsent(true)} data-testid="request-rn-btn"
              className="w-full flex items-center justify-center gap-2 bg-navy text-white py-4 rounded-full font-semibold hover:-translate-y-0.5 transition-all active:scale-[0.99]">
              <HeartPulse className="w-5 h-5" /> Request RN
            </button>
            <p className="mt-3 text-xs text-slate-400 text-center">You'll review and sign the required privacy &amp; consent forms next.</p>
          </>
        )}
      </div>

      {showConsent && (
        <ConsentModal caseId={target.case_id} forms={data.required_forms}
          onClose={() => setShowConsent(false)} onDone={() => { setShowConsent(false); load(); }} />
      )}
    </div>
  );
}

function ConsentModal({ caseId, forms, onClose, onDone }) {
  const [sigs, setSigs] = useState({});
  const [busy, setBusy] = useState(false);
  const allSigned = forms.every((f) => (sigs[f.key] || "").trim());

  const submit = async () => {
    if (!allSigned) { toast.error("Please sign every form to continue."); return; }
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/rn-intake/consent`, {
        signatures: forms.map((f) => ({ key: f.key, signature: sigs[f.key].trim() })),
      });
      toast.success("Thank you. We are locating your Crash Ready RN.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit consent");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="rn-consent-modal">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-cobalt-light flex items-center justify-center"><FileSignature className="w-5 h-5 text-cobalt" /></div>
            <div><h3 className="font-heading font-bold text-navy leading-tight">Privacy &amp; Consent</h3>
              <p className="text-xs text-slate-500">Sign each form to proceed with your nurse intake.</p></div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700">
            Placeholder legal language — final forms will be provided by healthcare counsel before production.
          </div>
          {forms.map((f, i) => {
            const signed = (sigs[f.key] || "").trim();
            return (
              <div key={f.key} className="rounded-2xl border border-slate-100 p-4" data-testid={`consent-form-${f.key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-navy text-sm">{i + 1}. {f.title}</span>
                  {signed && <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium"><Check className="w-3.5 h-3.5" /> Signed</span>}
                </div>
                <p className="mt-1.5 text-xs text-slate-500 leading-relaxed max-h-24 overflow-y-auto">{f.body}</p>
                <input value={sigs[f.key] || ""} onChange={(e) => setSigs({ ...sigs, [f.key]: e.target.value })}
                  data-testid={`consent-sign-${f.key}`} placeholder="Type your full legal name to sign"
                  className="mt-3 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" />
              </div>
            );
          })}
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-400">{forms.filter((f) => (sigs[f.key] || "").trim()).length}/{forms.length} signed</span>
          <button onClick={submit} disabled={busy || !allSigned} data-testid="consent-submit-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:-translate-y-0.5 transition-all active:scale-[0.99] disabled:opacity-50 disabled:translate-y-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Sign &amp; request nurse
          </button>
        </div>
      </div>
    </div>
  );
}
